import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Grid,
  Select,
  Accordion,
  Button,
  Group,
  Alert,
  Tooltip,
} from "@mantine/core";
import intl from "react-intl-universal";
import { Edit, FileSettings, Trash } from "tabler-icons-react";

import SetupStapelSignDialog, {
  UserStapel,
} from "./SetupStapelSignDialog";
import SelectedStapelSignDocsforUser from "./SelectedStapelSignDocsforUser";
import CountStapelSignDocsforUser from "./CountStapelSignDocsforUser";
import SetupPrepareStapelDocs from "./SetupPrepareStapelDocs";

/* =====================
   COMPONENT
===================== */

const SetupStapelSign = (props: any) => {
  const dispatch = useDispatch();

    const meetingDocs =
      useSelector((state: any) => state.user.meetingdocs) || [];
  
    const filteredDocs = useMemo(
      () => meetingDocs.filter((d: any) => d.isToBeSigned && d.isToBeRead),
      [meetingDocs]
    );

  const rawParticipants = useSelector((state: any) => state.user.meetinguser);
  const initparticipants = useMemo(() => rawParticipants ?? [], [rawParticipants]);

  const rawStapeluser = useSelector((state: any) => state.user.userhavetostapelsigndocs);
  const stapeluser = useMemo(() => rawStapeluser ?? [], [rawStapeluser]);

  /* =====================
     DERIVED STATE
  ===================== */

  const initdocstosign = filteredDocs;

  const disable = useMemo(
    () =>
      !initdocstosign.some(
        (doc: any) => doc.isToBeSigned === true
      ),
    [initdocstosign]
  );

  /* =====================
     LOCAL UI STATE
  ===================== */

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editdialog, setEditDialog] = useState(false);
  const [editingStapel, setEditingStapel] =
    useState<UserStapel | null>(null);

  const [participantId, setParticipantId] =
    useState<string | null>(null);
  const [participantName, setParticipantName] =
    useState<string | null>(null);

  /* =====================
     PARTICIPANTS
  ===================== */

  const initparticipantsdata = useMemo(
    () =>
      initparticipants.filter(
        (p: { role: string }) => p.role !== "gast"
      ),
    [initparticipants]
  );

  const participantsDropdownData = useMemo(
    () =>
      initparticipantsdata.map((p: any) => ({
        value: p.id,
        label:
          p.role === "notar"
            ? `${p.lastName} ${p.firstName} (Notar)`
            : `${p.lastName} ${p.firstName}`,
      })),
    [initparticipantsdata]
  );

  /* =====================
     LIST DATA (Accordion)
     ❗ KEIN GROUPING nach participantId
  ===================== */

  const participantsListData = useMemo(() => {
    const array = Array.isArray(stapeluser)
      ? stapeluser
      : Object.values(stapeluser);

    return array
      .filter(
        (s: any) =>
          Array.isArray(s.documentsSignModes) &&
          s.documentsSignModes.length > 0
      )
      .map((stapel: any) => {
        const participant = initparticipantsdata.find(
          (p: any) => p.id === stapel.participantId
        );

        return {
          key: stapel.id ?? stapel.localStapelId,
          stapelId: stapel.id,
          localStapelId: stapel.localStapelId,
          participantId: stapel.participantId,
          name: participant
            ? `${participant.lastName} ${participant.firstName}`
            : stapel.participantId,
          documentsCount: stapel.documentsSignModes.length,
          documentsSignModes: stapel.documentsSignModes,
        };
      });
  }, [stapeluser, initparticipantsdata]);

  /* =====================
     HANDLER
  ===================== */

  const selectParticipant = (value: string | null) => {
    setParticipantId(value);
    const p = initparticipantsdata.find(
      (x: any) => x.id === value
    );
    setParticipantName(
      p ? `${p.lastName} ${p.firstName}` : null
    );
  };

  const openDialog = () => {
    if (!participantId) return;

    setEditingStapel({
      participantId,
      documentsSignModes: [],
    });

    setEditDialog(false);
    setDialogOpen(true);
    props.setDisableonSigSetup(true);
  };

  const openEditDialog = (item: any) => {
    setEditingStapel({
      stapelId: item.stapelId,
      localStapelId: item.localStapelId,
      participantId: item.participantId,
      documentsSignModes: item.documentsSignModes ?? [],
    });

    setParticipantId(item.participantId);
    setParticipantName(item.name);
    setEditDialog(true);
    setDialogOpen(true);
    props.setDisableonSigSetup(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingStapel(null);
    props.setDisableonSigSetup(false);
  };

  /* =====================
     SAVE (NUR EIN STAPEL!)
  ===================== */

  const saveDialog = (stapel: UserStapel) => {
    if (!stapel) return;

    if (stapel.stapelId) {
      dispatch({
        type: "SIGNALR_UPDATESTAPELFORSIGN",
        payload: {
          StapelId: stapel.stapelId,
          documentsSignModes: stapel.documentsSignModes,
        },
      });
    } else {
      dispatch({
        type: "SIGNALR_CREATESTAPELFORSIGN",
        payload: {
          ParticipantId: stapel.participantId,
          documentsSignModes: stapel.documentsSignModes,
        },
      });
    }

    closeDialog();
  };

  const deleteStapel = (item: any) => {
    if (!item.stapelId) return;

    dispatch({
      type: "SIGNALR_REMOVESTAPEL",
      payload: { StapelId: item.stapelId },
    });
  };

  //console.log("Filtered Docs:", filteredDocs.length);

  /* =====================
     RENDER
  ===================== */

  return (
    <>
      {!dialogOpen && (
        <>
          {!disable && <p>{intl.get("STAPELDIALOG_INFO")}</p>}
          {disable && (
            <Alert color="red" title={intl.get("SETUPSTAPELSIGN_ALERT_HEAD")}>
              {intl.get("SETUPSTAPELSIGN_ALERT_CONTENT")}
            </Alert>
          )}

          <Grid>
            <Grid.Col span={4}>
              <Select
                disabled={disable}
                value={participantId}
                onChange={selectParticipant}
                data={participantsDropdownData}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Button
                disabled={disable || !participantId}
                onClick={openDialog}
              >
                {intl.get("STAPELDIALOG_BUTTON_NEW")}
              </Button>
            </Grid.Col>
          </Grid>

          <hr />

          <Accordion>
            {participantsListData.map((item: any) => (
              <Accordion.Item
                key={item.key}
                value={String(item.key)}
              >
                <Accordion.Control>
                  <Group>
                     <Tooltip label={intl.get('STAPELSETUP_TT_EDIT')} multiline={true}>
                      <span>
                        <Edit onClick={() => openEditDialog(item)} />
                      </span>
                      </Tooltip>
                      <Tooltip label={intl.get('STAPELSETUP_TT_SETUP')} multiline={true}>
                        <span>
                          <SetupPrepareStapelDocs item={item} />
                        </span>
                      </Tooltip>
                     <Tooltip label={intl.get('STAPELSETUP_TT_DELETE')} multiline={true}>
                        <span>
                        <Trash
                          color="red"
                          onClick={() => deleteStapel(item)}
                        />
                      </span>
                    </Tooltip>
                    <strong>{item.name}</strong>
                    <CountStapelSignDocsforUser
                      stapelId={item.stapelId}
                      localStapelId={item.localStapelId}
                    />
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <SelectedStapelSignDocsforUser
                    stapelId={item.stapelId}
                    localStapelId={item.localStapelId}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </>
      )}

      {dialogOpen && editingStapel && (
        <SetupStapelSignDialog
          participantId={editingStapel.participantId}
          participantName={participantName!}
          editdialog={editdialog}
          initialStapel={editingStapel}
          onSave={saveDialog}
          onClose={closeDialog}
        />
      )}
    </>
  );
};

export default SetupStapelSign;

